/**
 * Step 4 — Review, quality checks, generate.
 *
 * The summary is read-only on purpose: this is the last look before a document
 * goes to a client, and the way back to change something is the step link, not
 * an inline edit.
 *
 * The quality report comes from the server. The same function runs again inside
 * the generate endpoint, so a browser that skipped this screen still cannot
 * produce an incomplete proposal.
 */
import type {
  CommercialSummary,
  FinalizationReport,
  ProposalVersionSummary,
  Deviation,
  FamilyRules,
  ReviewReport,
  PaymentTerm,
  Proposal,
  ProposalConfiguration,
  QualityReport,
  TemplateManifest,
} from '../../lib/api';
import type { StepId } from './steps';
import { resolveValidityDate } from './ClientStep';
import { Card, Skeleton, formatDate, formatMoney } from '../../components/ui';
import { LeakagePanel, ReadinessPanel, VersionHistory } from './FinalizationPanels';

export function ReviewStep({
  proposal,
  config,
  manifest,
  rules,
  commercials,
  quality,
  review,
  finalization,
  versions,
  acknowledging,
  onAcknowledgeLeakage,
  onGoToStep,
}: {
  proposal: Proposal;
  config: ProposalConfiguration;
  manifest: TemplateManifest;
  rules: FamilyRules;
  commercials: CommercialSummary | null;
  quality: QualityReport | null;
  review: ReviewReport;
  finalization: FinalizationReport | null;
  versions: ProposalVersionSummary[];
  acknowledging: boolean;
  onAcknowledgeLeakage(findingIds: string[]): void;
  onGoToStep(step: StepId): void;
  paymentTerms?: PaymentTerm[];
}) {
  const currency = config.currency ?? 'PHP';


  return (
    <div className="space-y-6">
      <QualityPanel quality={quality} onGoToStep={onGoToStep} />

      <LeakagePanel
        report={finalization}
        busy={acknowledging}
        onGoToStep={onGoToStep}
        onAcknowledge={onAcknowledgeLeakage}
      />

      <ReadinessPanel report={finalization} onGoToStep={onGoToStep} />

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Client" actions={<EditLink onClick={() => onGoToStep('client')} />}>
          <dl className="space-y-2.5 text-[13px]">
            <Row label="Client" value={config.clientCompanyName || '—'} />
            <Row label="Proposal type" value={proposal.templateName} />
            <Row label="Proposal #" value={proposal.proposalNumber} />
          </dl>
        </Card>

        <Card title="Dates & owner" actions={<EditLink onClick={() => onGoToStep('client')} />}>
          <dl className="space-y-2.5 text-[13px]">
            <Row label="Proposal date" value={formatDate(config.proposalDate)} />
            <Row label="Valid until" value={formatDate(resolveValidityDate(config))} />
            <Row
              label="Account owner"
              value={
                config.accountOwnerName
                  ? `${config.accountOwnerName}, ${config.accountOwnerTitle ?? ''}`.replace(
                      /,\s*$/,
                      '',
                    )
                  : '—'
              }
            />
          </dl>
        </Card>
      </div>

      {rules.sections.length > 0 && (
        <Card
          title="Engagement"
          description="Scoping detail for this service. Not printed on the proposal."
          actions={<EditLink onClick={() => onGoToStep('engagement')} />}
        >
          <dl className="space-y-2.5 text-[13px]">
            {rules.sections.flatMap((section) =>
              section.fields.map((field) => {
                const value = config.engagement?.[field.key];
                const shown = Array.isArray(value)
                  ? value
                      .map(
                        (entry) =>
                          field.options?.find((o) => o.value === entry)?.label ?? entry,
                      )
                      .filter((entry) => entry !== '__other__')
                      .join(', ')
                  : typeof value === 'string'
                    ? (field.options?.find((o) => o.value === value)?.label ?? value)
                    : typeof value === 'number'
                      ? String(value)
                      : '';
                return (
                  <div key={field.key} className="flex items-baseline justify-between gap-4">
                    <dt className="shrink-0 text-muted">{field.label}</dt>
                    <dd
                      className={[
                        'text-right font-medium',
                        shown ? 'text-ink' : field.required ? 'text-red-700' : 'text-muted',
                      ].join(' ')}
                    >
                      {shown || (field.required ? 'Not set' : '—')}
                    </dd>
                  </div>
                );
              }),
            )}
          </dl>
        </Card>
      )}

      <Card title="Timeline" actions={<EditLink onClick={() => onGoToStep('timeline')} />}>
        <ul className="divide-y divide-rule text-[13px]">
          {manifest.timelinePhases.map((phase) => {
            const value = config.durations?.[phase.id];
            const shown =
              typeof value === 'number'
                ? `${value} ${value === 1 ? 'week' : 'weeks'}`
                : typeof value === 'string' && value
                  ? value
                  : phase.durationKind === 'ONGOING'
                    ? 'Ongoing'
                    : null;
            return (
              <li key={phase.id} className="flex items-baseline justify-between gap-4 py-2">
                <span className="text-ink">{phase.phase}</span>
                <span className={shown ? 'font-medium text-ink' : 'text-red-700'}>
                  {shown ?? 'Not set'}
                </span>
              </li>
            );
          })}
        </ul>
      </Card>

      <Card
        title="Commercials"
        actions={<EditLink onClick={() => onGoToStep('commercials')} />}
      >
        {commercials ? (
          <ul className="divide-y divide-rule text-[13px]">
            {commercials.lines.map((line) => (
              <li key={line.id} className="flex items-baseline justify-between gap-4 py-2">
                <span className="text-ink">
                  {line.label}
                  {line.discountPercent > 0 && (
                    <span className="ml-2 text-[12px] text-muted">
                      −{line.discountPercent}%
                    </span>
                  )}
                  {commercials.model === 'ONE_TIME_AND_RECURRING' && (
                    <span
                      className={[
                        'ml-2 rounded px-1.5 py-0.5 text-[11px]',
                        line.kind === 'RECURRING'
                          ? 'bg-sky-50 text-sky-800'
                          : 'bg-tint text-muted',
                      ].join(' ')}
                    >
                      {line.kind === 'RECURRING' ? 'recurring' : 'one-time'}
                    </span>
                  )}
                </span>
                <span
                  className={
                    line.net === null ? 'text-red-700' : 'font-medium tabular-nums text-ink'
                  }
                >
                  {line.net === null ? 'Not set' : line.amountText}
                </span>
              </li>
            ))}

            {commercials.model === 'ONE_TIME_AND_RECURRING' ? (
              <>
                <li className="flex items-baseline justify-between gap-4 py-2">
                  <span className="font-semibold text-ink">One-time investment</span>
                  <span className="font-semibold tabular-nums text-ink">
                    {formatMoney(commercials.oneTimeTotal, currency)}
                  </span>
                </li>
                <li className="flex items-baseline justify-between gap-4 py-2">
                  <span className="font-semibold text-ink">Monthly recurring</span>
                  <span className="font-semibold tabular-nums text-ink">
                    {commercials.recurringTotal > 0
                      ? `${formatMoney(commercials.recurringTotal, currency)} ${commercials.recurringSuffix}`
                      : '—'}
                  </span>
                </li>
                {commercials.yearOneContractValue !== null && (
                  <li className="flex items-baseline justify-between gap-4 py-2">
                    <span className="text-muted">
                      Year 1 indicative contract value
                      <span className="ml-1.5 text-[11px]">(internal, not printed)</span>
                    </span>
                    <span className="font-medium tabular-nums text-ink">
                      {formatMoney(commercials.yearOneContractValue, currency)}
                    </span>
                  </li>
                )}
              </>
            ) : (
              <li className="flex items-baseline justify-between gap-4 py-3">
                <span className="font-semibold text-ink">{manifest.commercialTotalLabel}</span>
                <span className="text-[15px] font-semibold tabular-nums text-ink">
                  {commercials.totalText}
                </span>
              </li>
            )}
          </ul>
        ) : (
          <Skeleton className="h-32 w-full" />
        )}

        <div className="mt-3 border-t border-rule pt-3 text-[13px]">
          <Row label="Payment terms" value={config.paymentTerms || '—'} />
        </div>
      </Card>

      <DeviationPanel report={review} onGoToStep={onGoToStep} />

      <Card
        title="Included by default"
        description="Approved content that appears without any input from you."
      >
        <ul className="grid gap-2 text-[13px] sm:grid-cols-2">
          {[
            `Scope of work — ${manifest.scopeGroups.reduce((n, g) => n + g.items.length, 0)} approved items`,
            `Deliverables — ${manifest.deliverables.length} items`,
            `Our approach — ${manifest.approachPhases.length} phases`,
            'Why Lotus Tech Solutions — differentiators and metrics',
            'Assumptions & terms',
            'Next steps and acceptance block',
          ].map((entry) => (
            <li key={entry} className="flex gap-1.5 text-body">
              <span aria-hidden="true" className="text-teal">
                ✓
              </span>
              <span>{entry}</span>
            </li>
          ))}
        </ul>
      </Card>

      <VersionHistory versions={versions} />
    </div>
  );
}

function QualityPanel({
  quality,
  onGoToStep,
}: {
  quality: QualityReport | null;
  onGoToStep(step: StepId): void;
}) {
  if (!quality) return <Skeleton className="h-32 w-full" />;

  const ready = quality.status === 'READY_TO_GENERATE';
  const blockers = quality.findings.filter((f) => f.severity === 'BLOCKER');
  const warnings = quality.findings.filter((f) => f.severity === 'WARNING');

  return (
    <section
      className={[
        'rounded-lg border p-5',
        ready ? 'border-emerald-200 bg-emerald-50' : 'border-amber-200 bg-amber-50',
      ].join(' ')}
    >
      <div className="flex flex-wrap items-center gap-3">
        <span
          className={[
            'inline-flex items-center gap-2 rounded-full px-3 py-1 text-[12px] font-semibold uppercase tracking-wide',
            ready ? 'bg-emerald-700 text-white' : 'bg-amber-700 text-white',
          ].join(' ')}
        >
          {ready ? 'Ready to generate' : 'Needs attention'}
        </span>
        {!ready && (
          <span className="text-[13px] text-amber-900">
            {blockers.length} item{blockers.length === 1 ? '' : 's'} to resolve
          </span>
        )}
      </div>

      <ul className="mt-4 grid gap-1.5 sm:grid-cols-2">
        {quality.checks.map((check) => (
          <li key={check.check} className="flex items-center gap-2 text-[13px]">
            <span
              aria-hidden="true"
              className={check.passed ? 'text-emerald-700' : 'text-amber-800'}
            >
              {check.passed ? '✓' : '○'}
            </span>
            <span className={check.passed ? 'text-body' : 'font-medium text-amber-900'}>
              {check.label}
            </span>
          </li>
        ))}
      </ul>

      {blockers.length > 0 && (
        <ul className="mt-4 space-y-1.5 border-t border-amber-200 pt-3">
          {blockers.map((finding, index) => (
            <li key={`${finding.check}-${index}`} className="text-[13px] text-amber-900">
              {finding.message}{' '}
              <button
                type="button"
                onClick={() => onGoToStep(finding.step as StepId)}
                className="font-semibold underline"
              >
                Fix
              </button>
            </li>
          ))}
        </ul>
      )}

      {warnings.length > 0 && (
        <div className="mt-3">
          {warnings.map((finding, index) => (
            <p key={index} className="text-[12px] text-amber-800">
              Warning: {finding.message}
            </p>
          ))}
        </div>
      )}
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="shrink-0 text-muted">{label}</dt>
      <dd className="text-right font-medium text-ink">{value}</dd>
    </div>
  );
}

function EditLink({ onClick }: { onClick(): void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded px-2 py-1 text-[13px] font-medium text-teal hover:bg-tint"
    >
      Edit
    </button>
  );
}

/**
 * How far this proposal departs from the approved baseline.
 *
 * Grouped by kind with counts and short examples, so a salesperson can see at a
 * glance what a reviewer will ask about — and jump straight to the step that
 * produced it.
 */
function DeviationPanel({
  report,
  onGoToStep,
}: {
  report: ReviewReport;
  onGoToStep(step: StepId): void;
}) {
  const { deviations } = report;

  if (deviations.isBaseline) {
    return (
      <Card title="Changes to approved content">
        <p className="flex items-center gap-2 text-[13px] text-body">
          <span aria-hidden="true" className="text-emerald-700">
            ✓
          </span>
          This proposal uses the approved template exactly as it stands. Nothing has been
          edited, excluded or added.
        </p>
      </Card>
    );
  }

  const tone = (kind: Deviation['kind']) =>
    kind.startsWith('CUSTOM')
      ? 'bg-sky-50 text-sky-800 ring-sky-200'
      : kind.startsWith('EXCLUDED')
        ? 'bg-neutral-100 text-neutral-700 ring-neutral-200'
        : 'bg-amber-50 text-amber-900 ring-amber-200';

  return (
    <Card
      title="Changes to approved content"
      description={`${deviations.total} departure${deviations.total === 1 ? '' : 's'} from the approved baseline.`}
    >
      <ul className="space-y-2.5">
        {deviations.deviations.map((deviation) => (
          <li
            key={deviation.kind}
            className={[
              'rounded-md px-3 py-2.5 text-[13px] ring-1 ring-inset',
              tone(deviation.kind),
            ].join(' ')}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-semibold">
                {deviation.count} × {deviation.label}
              </span>
              <button
                type="button"
                onClick={() => onGoToStep(deviation.step as StepId)}
                className="text-[12px] font-medium underline"
              >
                Review
              </button>
            </div>
            <ul className="mt-1 space-y-0.5">
              {deviation.examples.map((example, index) => (
                <li key={index} className="text-[12px] opacity-90">
                  · {example}
                </li>
              ))}
              {deviation.count > deviation.examples.length && (
                <li className="text-[12px] opacity-70">
                  · and {deviation.count - deviation.examples.length} more
                </li>
              )}
            </ul>
          </li>
        ))}
      </ul>

      <p className="mt-3 border-t border-rule pt-3 text-[12px] text-muted">
        Every change is recorded on the proposal's audit trail. Administrator-controlled
        content — differentiators, corporate claims, confidentiality and legal terms, contact
        details and next steps — is unchanged.
      </p>
    </Card>
  );
}
